import {
	BrainCircuit,
	BookOpenCheck,
	ClipboardCheck,
	Database,
	MoonStar,
	SlidersHorizontal,
	SunMedium,
} from "lucide-react";
import type React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useThemeStore } from "@/lib/stores/theme";
import { AppRoutes } from "@/router/routes";

export const App: React.FC = () => {
	const { mode, toggle } = useThemeStore();
	const location = useLocation();
	const isAdminRoute =
		location.pathname.startsWith("/admin") ||
		location.pathname === "/assessment" ||
		location.pathname === "/training-config" ||
		location.pathname === "/theory";

	if (!isAdminRoute) {
		return <AppRoutes />;
	}

	return (
		<div className="native-app-scroll h-[100dvh] overflow-y-auto bg-background text-foreground">
			<header className="sticky top-0 z-30 border-b border-border/70 bg-background/82 backdrop-blur-xl">
				<div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-4">
					<div className="flex min-w-0 items-center gap-4">
						<div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-lg shadow-primary/20">
							<BrainCircuit className="size-5" />
						</div>
						<div className="min-w-0">
							<p className="truncate text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
								training admin
							</p>
							<p className="truncate text-base font-medium text-foreground">轻训练后台配置</p>
						</div>
					</div>

					<div className="flex items-center gap-3">
						<nav className="hidden items-center gap-2 md:flex">
							<NavButton to="/admin/questions">
								<Database />
								题库录入
							</NavButton>
							<NavButton to="/admin/training-config">
								<SlidersHorizontal />
								训练配置
							</NavButton>
							<NavButton to="/admin/assessment">
								<ClipboardCheck />
								做题体验
							</NavButton>
							<NavButton to="/admin/theory">
								<BookOpenCheck />
								理论核心
							</NavButton>
						</nav>
						<Badge variant="secondary" className="hidden sm:inline-flex">
							{mode === "light" ? "Light mode" : "Dark mode"}
						</Badge>
						<Button onClick={toggle} variant="outline" size="sm">
							{mode === "light" ? <MoonStar /> : <SunMedium />}
							<span>{mode === "light" ? "切到深色" : "切到浅色"}</span>
						</Button>
					</div>
				</div>
			</header>

			<main className="mx-auto max-w-7xl px-6 py-8 sm:py-10">
				<AppRoutes />
			</main>

			<footer className="border-t border-border/60 bg-background/75">
				<div className="mx-auto flex max-w-7xl flex-col gap-2 px-6 py-6 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
					<p>状态识别问题、训练动作、理论依据和竞品证据的本地题库。</p>
					<p>© {new Date().getFullYear()} Psychology Training Admin</p>
				</div>
			</footer>
		</div>
	);
};

function NavButton({ to, children }: { to: string; children: React.ReactNode }) {
	return (
		<NavLink
			to={to}
			className={({ isActive }) =>
				[
					"inline-flex h-9 items-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors",
					isActive
						? "border-primary bg-primary text-primary-foreground"
						: "border-border bg-background text-foreground hover:bg-muted",
				].join(" ")
			}
		>
			{children}
		</NavLink>
	);
}
